import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Users, ShieldCheck } from "lucide-react";
import {
  useListAdminUsers,
  useUpdateAdminUser,
  getListAdminUsersQueryKey,
  type AdminUser,
} from "@workspace/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toastError } from "@/lib/toast-error";
import { toast } from "sonner";

export function AdminUsersSection({ currentUserId }: { currentUserId?: string | null }) {
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useListAdminUsers({
    query: { queryKey: getListAdminUsersQueryKey() },
  });
  const updateUser = useUpdateAdminUser();

  const handleToggle = (user: AdminUser, next: boolean) => {
    updateUser.mutate(
      { id: user.id, data: { isAdmin: next } },
      {
        onSuccess: (updated) => {
          queryClient.setQueryData<AdminUser[] | undefined>(
            getListAdminUsersQueryKey(),
            (prev) =>
              prev?.map((u) => (u.id === updated.id ? updated : u)) ?? prev,
          );
          toast.success(
            next
              ? `Granted admin access to ${updated.email}`
              : `Revoked admin access from ${updated.email}`,
          );
        },
        onError: (err) => toastError(err, "Failed to update admin access"),
      },
    );
  };

  return (
    <div className="p-4 border rounded-lg bg-muted/20 space-y-3" data-testid="section-admin-users">
      <div className="flex items-start gap-3">
        <Users className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <h4 className="font-medium">Admin Access</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Grant or revoke admin access for users who have signed in. Users
            configured via the <code className="font-mono text-xs">ADMIN_EMAILS</code> environment
            variable cannot be demoted from here.
          </p>
        </div>
      </div>
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : !users || users.length === 0 ? (
        <p className="text-sm text-muted-foreground">No users yet.</p>
      ) : (
        <ul className="divide-y rounded-md border bg-background">
          {users.map((u) => {
            const isSelf = u.id === currentUserId;
            const locked = u.isFromEnv || (isSelf && u.isAdmin);
            const lockReason = u.isFromEnv
              ? "Set via ADMIN_EMAILS env variable"
              : isSelf && u.isAdmin
                ? "You can't revoke your own admin access"
                : null;
            return (
              <li
                key={u.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
                data-testid={`row-admin-user-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">
                      {u.name || u.email}
                    </span>
                    {u.isFromEnv && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                        <ShieldCheck className="h-3 w-3" />
                        env
                      </span>
                    )}
                    {isSelf && (
                      <span className="rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        you
                      </span>
                    )}
                  </div>
                  {u.name && (
                    <div className="text-xs text-muted-foreground truncate">
                      {u.email}
                    </div>
                  )}
                  {lockReason && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {lockReason}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {updateUser.isPending && updateUser.variables?.id === u.id && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  )}
                  <Switch
                    checked={u.isAdmin}
                    disabled={locked || updateUser.isPending}
                    onCheckedChange={(v) => handleToggle(u, v)}
                    aria-label={`Toggle admin for ${u.email}`}
                    data-testid={`switch-admin-${u.id}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
